/**
 * ログイン直後の画面（ホーム・`/`）の組み立て（2026-09-28 creator/home-redesign・founder の感想・最優先）。
 * 出し分けの判断は homeFlow.ts（純関数・テストあり）、部品は HomeParts.tsx・HomeAsk.tsx、文言は i18n/en.home.ts・ja.home.ts、
 * 見た目は styles.css の「home」の節。
 *
 * 形は 3 つ（homeFlow.ts の HOME_VARIANT 1 か所で切り替え・既定は C = HomeC.tsx）:
 *   A: 言い切りの見出し ＋ 3 つの柱（3 列・「用意済み」）＋「はじめの一歩」の段（いまの段だけ開く・主なボタンは 1 つ）
 *   B: 問いかけ「何を作る？」＋ 1 行の入力欄 → AI エージェントに貼れる頼み方の文とコピー（主な行動）。柱は見出しの下の小さな 1 行、
 *      「つなぐ」は入力欄の下の小さな 1 行、メール確認・入金はその下に控えめに
 * どちらも: 柱はリンクにしない・ON/OFF を見せない（「選んで使う」に見せない。設定・利用料はゲームの画面の奥と「お金」の画面にある）。
 *   ゲームがある人は見出しを小さく、準備が欠けているときだけ知らせ、あとは自分のゲームの一覧
 * 作成フォームの中身（CreateProject）は ProjectsPage.tsx にある（creator/slug-rename の持ち場）= ここは入口のボタンと置き場所だけ
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CONNECT_PATH } from '../connectPage';
import type { ProjectSummary } from '../api';
import { HOME_VARIANT, homeLayout, homeSteps, inviteVerifyAmount, nextSetupStep, setupNotices, type HomeInput, type HomeVariant } from '../homeFlow';
import { useT } from '../i18n';
import { HeroAsk } from './HomeAsk';
import { ConnectBody, ProjectGrid, HeroStatement, Pillars, SetupNotice, StepItem } from './HomeParts';
import { HomeC } from './HomeC';

export interface HomeProps {
  projects: ProjectSummary[];
  flow: HomeInput;
  /** GET /v1/platform/info の mcpUrl（homeFlow.ts mcpEndpoint で検査済み・オリジンだけなら /mcp を足した値）。null = ガイドへのリンクを出す */
  mcpUrl: string | null;
  creating: boolean;
  onCreate: () => void;
  /** 作成フォーム（ProjectsPage の CreateProject）。creating のときだけ渡される */
  createForm: ReactNode;
  /** 最初のテスト版のカード（いちばん新しいプロジェクトの名前と遊べる URL）。null = 出さない（homeFlow.ts firstTestUrl）。C だけが使う */
  firstTest?: { name: string; url: string } | null;
  /** 見比べ用に上書きできる（普段は HOME_VARIANT） */
  variant?: HomeVariant;
}

export function HomeStart({ projects, flow, mcpUrl, creating, onCreate, createForm, firstTest = null, variant = HOME_VARIANT }: HomeProps) {
  const { t } = useT();
  const layout = homeLayout(variant);
  // C（既定）は組み立てが違う（場所の名前の見出し・番号つきの手順）ので HomeC.tsx に任せる
  if (layout.hero === 'page') return <HomeC projects={projects} flow={flow} mcpUrl={mcpUrl} creating={creating} onCreate={onCreate} createForm={createForm} firstTest={firstTest} />;
  const hasGames = projects.length > 0;
  const next = hasGames ? nextSetupStep(flow) : null;
  const notices = setupNotices(flow);

  // 画面から作りたい人のための入口（主な道は AI エージェント）。控えめなリンク
  const createLink = !creating && (
    <p className="home-alt">
      <button type="button" className="link-button" onClick={onCreate}>
        {t('home.createHere')}
      </button>
    </p>
  );

  return (
    <div className={`home home-${variant} ${hasGames ? 'home-compact' : ''}`}>
      {layout.hero === 'statement' ? <HeroStatement lead={!hasGames} /> : <HeroAsk compact={hasGames} />}

      {layout.pillars === 'columns' && <Pillars />}

      {/* ---------------- A: 段の並び */}
      {layout.steps === 'flow' && !hasGames && (
        <section className="home-steps-wrap" aria-labelledby="home-steps-title">
          <h2 id="home-steps-title" className="home-section-title">
            {t('home.stepsTitle')}
          </h2>
          <ol className="home-steps">
            {homeSteps(flow).map((s, i) => (
              <StepItem key={s.id} step={s} n={i + 1} mcpUrl={mcpUrl} invite={inviteVerifyAmount(flow)} />
            ))}
          </ol>
          {createLink}
          {createForm}
        </section>
      )}
      {layout.steps === 'flow' && hasGames && next && (
        <section className="home-steps-wrap" aria-labelledby="home-next-title">
          <h2 id="home-next-title" className="home-section-title">
            {t('home.nextTitle')}
          </h2>
          <ol className="home-steps">
            <StepItem step={{ id: next, state: 'current' }} mcpUrl={mcpUrl} invite={inviteVerifyAmount(flow)} />
          </ol>
        </section>
      )}

      {/* ---------------- B: つなぐ 1 行 ＋ 準備（控えめ） */}
      {layout.connect === 'line' && (
        <div className="home-below">
          {flow.connected === true ? (
            <p className="home-connected">
              <span className="home-ready-dot" aria-hidden="true" />
              {t('home.b.connected')}
              {/* B の「つながっています」にも /connect への道（HomeC と同じ・2026-10-07 console/connect-again） */}
              <Link to={CONNECT_PATH} className="home-connect-again">
                {t('connect.elsewhereLink')}
              </Link>
            </p>
          ) : (
            // 未確認の人はまだつなげない（kdt_ が作れない・403）ので、下の確認の案内だけを出す
            flow.emailVerified !== false && (
              <section className="home-connect-line" aria-labelledby="home-connect-title">
                <h2 id="home-connect-title" className="home-section-title">
                  {t('home.b.connectLabel')}
                </h2>
                <ConnectBody mcpUrl={mcpUrl} compact />
              </section>
            )
          )}
          {notices.length > 0 && (
            <section aria-labelledby="home-setup-title">
              <h2 id="home-setup-title" className="home-section-title">
                {t('home.b.setupLabel')}
              </h2>
              <ul className="home-setups">
                {notices.map((id) => (
                  <SetupNotice key={id} id={id} invite={inviteVerifyAmount(flow)} />
                ))}
              </ul>
            </section>
          )}
          {!hasGames && createLink}
          {!hasGames && createForm}
        </div>
      )}

      {hasGames && (
        <section className="home-games" aria-labelledby="home-games-title">
          <div className="home-games-head">
            <h2 id="home-games-title" className="home-section-title">
              {t('home.titleWithProjects')}
            </h2>
            {!creating && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={onCreate}>
                {t('home.newProject')}
              </button>
            )}
          </div>
          {createForm}
          <ProjectGrid projects={projects} />
        </section>
      )}
    </div>
  );
}
